/**
 * 3D Buildings Layer - Powered by 3DBAG
 *
 * This component displays 3D building models for the Netherlands on the map using data from
 * the 3DBAG project. It was built by studying how the official 3DBAG viewer works and
 * adapting it to work with Rana's map system.
 *
 * Learn more:
 * - 3DBAG
 *   - 3DBAG Website: https://3dbag.nl/
 *   - 3DBAG Viewer Source Code: https://github.com/3DBAG/3dbag-viewer/blob/main/src/components/ThreeViewer.vue
 * - Technical sources:
 *   - Maplibre 3D tiles integration: https://maplibre.org/maplibre-gl-js/docs/examples/add-3d-tiles-using-threejs/
 *   - Three.js 3D Tiles Renderer: https://github.com/NASA-AMMOS/3DTilesRendererJS
 *   - Three.js: https://threejs.org/
 *
 * What it does:
 * 1. Downloads compressed 3D building models from 3DBAG's servers
 * 2. Decompresses them using special algorithms (meshopt) so they're smaller and faster
 * 3. Positions each building on the WGS84 ellipsoid (aligned with the map's terrain source)
 * 4. Renders everything as a 3D scene inside the map using Three.js
 * 5. Styles the buildings with a light stone color and makes them slightly transparent
 * 6. Adds lighting so the buildings look good even in shadows
 */

import { TilesRenderer, WGS84_ELLIPSOID } from "3d-tiles-renderer/three";
import { GLTFExtensionsPlugin } from "3d-tiles-renderer/three/plugins";
import type { CustomLayerInterface, Map as MapLibreMap } from "maplibre-gl";
import { MercatorCoordinate } from "maplibre-gl";
import { useRef } from "react";
import { Layer } from "react-map-gl/maplibre";
import {
  AmbientLight,
  DirectionalLight,
  MathUtils,
  Matrix4,
  Mesh,
  MeshLambertMaterial,
  Object3D,
  PerspectiveCamera,
  PointLight,
  Vector3,
  WebGLRenderer,
} from "three";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";

import { BEFORE_BUILDING_LAYER_ID, BUILDING_LAYER_ID } from "../consts/building-layer-ids";

interface Rana3DBuildingsLayerProps {
  visible: boolean;
}

type MapLibreRenderArgs = {
  defaultProjectionData?: {
    mainMatrix?: number[];
    projectionMatrix?: number[];
  };
  projectionMatrix?: number[];
  matrix?: number[];
};

const TILESET_URL = "https://data.3dbag.nl/v20250903/cesium3dtiles/lod22/tileset.json";
const BUILDING_COLOR = "#757373";
const BUILDING_OPACITY = 1;
const AMBIENT_LIGHT = new AmbientLight(0xffffff, 4);
const POINT_LIGHT = new PointLight(0xffffff, 2, 0, 1);
const DIRECTIONAL_LIGHT = new DirectionalLight(0xffffff, 4);

function setEcefToLocalFrame(
  ellipsoid: typeof WGS84_ELLIPSOID,
  ellipsoidGroup: {
    matrix: Matrix4;
    matrixAutoUpdate: boolean;
    updateMatrixWorld: (force?: boolean) => void;
  },
  lat: number,
  lon: number,
  height = 0
) {
  const origin = ellipsoid.getCartographicToPosition(lat, lon, height, new Vector3());
  const sinLon = Math.sin(lon);
  const cosLon = Math.cos(lon);
  const sinLat = Math.sin(lat);
  const cosLat = Math.cos(lat);

  const rotation = new Matrix4().set(
    -sinLon,
    cosLon,
    0,
    0,
    cosLat * cosLon,
    cosLat * sinLon,
    sinLat,
    0,
    sinLat * cosLon,
    sinLat * sinLon,
    -cosLat,
    0,
    0,
    0,
    0,
    1
  );

  const translation = new Matrix4().makeTranslation(-origin.x, -origin.y, -origin.z);
  ellipsoidGroup.matrix.copy(rotation.multiply(translation));
  ellipsoidGroup.matrixAutoUpdate = false;
  ellipsoidGroup.updateMatrixWorld(true);
}

function updateLocalFrame(map: MapLibreMap, tiles: TilesRenderer, localTransform: Matrix4) {
  const center = map.getCenter();
  const lat = MathUtils.degToRad(center.lat);
  const lon = MathUtils.degToRad(center.lng);

  setEcefToLocalFrame(tiles.ellipsoid || WGS84_ELLIPSOID, tiles.group, lat, lon);

  const mercator = MercatorCoordinate.fromLngLat([center.lng, center.lat], 0);
  const scale = mercator.meterInMercatorCoordinateUnits();

  localTransform
    .makeTranslation(mercator.x, mercator.y, mercator.z)
    .scale(new Vector3(scale, -scale, scale))
    .multiply(new Matrix4().makeRotationX(Math.PI / 2));
}

function applyBuildingAppearance(scene: Object3D) {
  scene.traverse((child) => {
    const mesh = child as Mesh;
    if (!mesh.isMesh || !mesh.material) return;

    // Replace with MeshLambertMaterial which responds to all light types
    const newMaterial = new MeshLambertMaterial({
      color: BUILDING_COLOR,
      transparent: true,
      opacity: BUILDING_OPACITY,
      depthWrite: true,
      side: 0,
    });

    mesh.material = newMaterial;
  });
}

export function Rana3DBuildingsLayer({ visible }: Rana3DBuildingsLayerProps) {
  // Everything below lives in a ref rather than state. The layer object needs to stay stable
  // across renders, otherwise MapLibre thinks it's a new layer and removes/re-adds it, which is
  // expensive and causes flickering. Refs let us keep that stability while still being able to
  // read and update these values from inside the render function.
  const mapRef = useRef<MapLibreMap | null>(null);
  const tilesRef = useRef<TilesRenderer | null>(null);
  const rendererRef = useRef<WebGLRenderer | null>(null);
  const cameraRef = useRef<PerspectiveCamera | null>(null);
  const decoderReadyRef = useRef<Promise<void> | null>(null);
  const layerRef = useRef<CustomLayerInterface | null>(null);
  const localTransformRef = useRef(new Matrix4());
  const projectionMatrixRef = useRef(new Matrix4());
  const viewMatrixRef = useRef(new Matrix4());
  const renderProjectionMatrixRef = useRef(new Matrix4());
  const rendererSizeRef = useRef({ width: 0, height: 0 });
  const visibleRef = useRef(visible);
  const tilesInitStartedRef = useRef(false);
  const sceneRootRef = useRef(new Object3D());

  visibleRef.current = visible;

  if (!decoderReadyRef.current) {
    decoderReadyRef.current = MeshoptDecoder.ready;
  }

  if (!layerRef.current) {
    layerRef.current = {
      id: BUILDING_LAYER_ID,
      type: "custom",
      renderingMode: "3d",
      onAdd: (mapLibreMap, gl) => {
        mapRef.current = mapLibreMap;

        rendererRef.current = new WebGLRenderer({
          canvas: mapLibreMap.getCanvas(),
          context: gl,
          antialias: true,
        });
        rendererRef.current.autoClear = false;

        const canvas = mapLibreMap.getCanvas();
        // Keep Three's internal drawing buffer size in sync without mutating CSS
        // dimensions on the shared MapLibre canvas.
        rendererRef.current.setSize(canvas.width, canvas.height, false);
        rendererSizeRef.current = { width: canvas.width, height: canvas.height };

        cameraRef.current = new PerspectiveCamera(45, canvas.width / canvas.height, 0.1, 100000000);
        cameraRef.current.matrixAutoUpdate = false;
        cameraRef.current.matrixWorldAutoUpdate = false;
      },
      render: (gl, args) => {
        if (!rendererRef.current || !cameraRef.current || !mapRef.current) {
          return;
        }

        // Nothing to draw while the buildings are hidden. This also avoids downloading the
        // (large) 3DBAG tileset until the buildings are actually shown for the first time.
        if (!visibleRef.current) {
          return;
        }

        if (!tilesInitStartedRef.current) {
          tilesInitStartedRef.current = true;

          void decoderReadyRef.current?.then(() => {
            const tilesRenderer = new TilesRenderer(TILESET_URL);
            tilesRenderer.registerPlugin(
              new GLTFExtensionsPlugin({
                metadata: true,
                meshoptDecoder: MeshoptDecoder,
              })
            );

            tilesRef.current = tilesRenderer;
            sceneRootRef.current.add(tilesRenderer.group);

            if (!sceneRootRef.current.getObjectByName("rana-3dbag-ambient-light")) {
              AMBIENT_LIGHT.name = "rana-3dbag-ambient-light";
              sceneRootRef.current.add(AMBIENT_LIGHT);

              POINT_LIGHT.name = "rana-3dbag-point-light";
              sceneRootRef.current.add(POINT_LIGHT);

              DIRECTIONAL_LIGHT.name = "rana-3dbag-directional-light";
              DIRECTIONAL_LIGHT.position.set(1, 1, 2);
              sceneRootRef.current.add(DIRECTIONAL_LIGHT);
            }

            if (cameraRef.current && rendererRef.current) {
              tilesRenderer.setCamera(cameraRef.current);
              tilesRenderer.setResolutionFromRenderer(cameraRef.current, rendererRef.current);
            }

            const requestRender = () => mapRef.current?.triggerRepaint();
            tilesRenderer.addEventListener("needs-update", requestRender);
            tilesRenderer.addEventListener("needs-render", requestRender);
            tilesRenderer.addEventListener("load-root-tileset", requestRender);
            tilesRenderer.addEventListener("load-model", (event) => {
              if (event.scene) {
                applyBuildingAppearance(event.scene);
                requestRender();
              }
            });

            tilesRenderer.update();
            requestRender();
          });
        }

        if (!tilesRef.current) {
          return;
        }

        const canvas = mapRef.current.getCanvas();
        if (
          canvas.width !== rendererSizeRef.current.width ||
          canvas.height !== rendererSizeRef.current.height
        ) {
          rendererRef.current.setSize(canvas.width, canvas.height, false);
          rendererSizeRef.current = { width: canvas.width, height: canvas.height };
        }

        updateLocalFrame(mapRef.current, tilesRef.current, localTransformRef.current);

        const projectionMatrixArray =
          (args as MapLibreRenderArgs).defaultProjectionData?.projectionMatrix ??
          (args as MapLibreRenderArgs).projectionMatrix ??
          (args as MapLibreRenderArgs).matrix;
        const mainMatrixArray =
          (args as MapLibreRenderArgs).defaultProjectionData?.mainMatrix ??
          (args as MapLibreRenderArgs).matrix ??
          (args as MapLibreRenderArgs).projectionMatrix;

        if (!projectionMatrixArray || !mainMatrixArray) {
          return;
        }

        projectionMatrixRef.current.fromArray(projectionMatrixArray);
        renderProjectionMatrixRef.current
          .fromArray(mainMatrixArray)
          .multiply(localTransformRef.current);
        viewMatrixRef.current
          .copy(projectionMatrixRef.current)
          .invert()
          .multiply(renderProjectionMatrixRef.current);

        cameraRef.current.projectionMatrix.copy(projectionMatrixRef.current);
        cameraRef.current.projectionMatrixInverse.copy(projectionMatrixRef.current).invert();
        cameraRef.current.matrixWorldInverse.copy(viewMatrixRef.current);
        cameraRef.current.matrixWorld.copy(viewMatrixRef.current).invert();
        cameraRef.current.position.setFromMatrixPosition(cameraRef.current.matrixWorld);

        tilesRef.current.setCamera(cameraRef.current);
        tilesRef.current.setResolutionFromRenderer(cameraRef.current, rendererRef.current);
        tilesRef.current.update();

        rendererRef.current.resetState();
        rendererRef.current.render(sceneRootRef.current, cameraRef.current);
      },
      onRemove: () => {
        if (tilesRef.current) {
          tilesRef.current.dispose();
          tilesRef.current = null;
        }
        if (rendererRef.current) {
          rendererRef.current.dispose();
          rendererRef.current = null;
        }
        cameraRef.current = null;
        mapRef.current = null;
      },
    };
  }

  // The layer is always mounted (regardless of `visible`) so its id stays registered on the map
  // and other layers can keep anchoring to it via `beforeId={BUILDING_LAYER_ID}`. Actual
  // visibility is handled inside `render` above.
  return layerRef.current ? (
    <Layer {...layerRef.current} beforeId={BEFORE_BUILDING_LAYER_ID} />
  ) : null;
}
